<template>
    <div class="graph-panel bg-primary bg-transparent" v-if="displayPanel">
        <div class="top-bar">
            <div>
                <div class="title">{{ title }}</div>
                <div class="subtitle">{{ subtitle }}</div>
            </div>
            <div>
                <a href="#" @click="hidePanel" class="btn btn-close">close</a>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div class="chart-title">All patients</div>
                <year-chart
                        :chartData="chartData"
                        :options="chartOptions"
                        v-show="displayChart"/>
                <div
                        class="chart-empty"
                        v-show="!displayChart">
                    <span>Data unavailable</span>
                </div>
            </div>
            <div class="col" v-if="queryRegionMode != 'state'">
                <div class="chart-title">Most urgent patients</div>
                <year-chart
                        :chartData="chartData1a"
                        :options="chartOptions"
                        v-show="displayChart1a"/>
                <div
                        class="chart-empty"
                        v-show="!displayChart1a">
                    <span>Data unavailable</span>
                </div>
            </div>
            <div class="col" v-if="queryRegionMode != 'state'">
                <div class="chart-title">Less urgent patients</div>
                <year-chart
                        :chartData="chartData1b"
                        :options="chartOptions"
                        v-show="displayChart1b"></year-chart>
                <div
                        class="chart-empty"
                        v-show="!displayChart1b">
                    <span>Data unavailable</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import hospitalsStatFun from '../../api/hospital'
import regionsStatFun from '../../api/region'
import txCentersStatFun from '../../api/txc'
import YearChart from '../Charts/YearChart'

export default {
  name: 'GraphPanel',

  components: { YearChart },

  computed: {
    ...mapGetters({
      allLegends: 'allLegends',
      hospitalRecords: 'hospitalRecords',
      queryOrganMode: 'queryOrganMode',
      queryRegionMode: 'queryRegionMode',
      queryStatisticId: 'queryStatisticId',
      queryYear: 'queryYear',
      txCenters: 'txCenters',
      txRecords: 'txRecords',
      selectedHospital: 'selectedHospital',
      selectedRegion: 'selectedRegion',
      selectedStatistic: 'selectedStatistic',
      selectedStatisticId: 'selectedStatisticId',
      selectedTxCenter: 'selectedTxCenter',
      stateShapes: 'stateShapes',
      opoShapes: 'opoShapes',
      unosShapes: 'unosShapes'
    }),

    chartOptions: function () {
      let self = this
      let statUnit = this.allLegends[self.selectedStatistic.legend].unit
      let options = {
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: self.selectedStatistic.legend !== 'percent',
                labelString: statUnit
              },
              ticks: {
                beginAtZero: true,
                min: 0,
                max: (self.selectedStatistic.legend === 'percent') ? 1 : this.chartDataYmax,
                callback: function (value) {
                  return (self.selectedStatistic.legend === 'percent')
                    ? (Math.round(value * 100) + '%')
                    : (Math.round(value * 100) / 100)
                }
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: function (item) {
              return (self.selectedStatistic.legend === 'percent')
                ? (Math.round(item.yLabel * 100) + '%')
                : (Math.round(item.yLabel * 100) / 100)
            }
          },
          displayColors: false
        }
      }
      return options
    },

    displayChart: function () {
      let cnt = this.selectionData.hist.length
      let allNaN = true
      for (let i = 0; i < cnt; i++) {
        allNaN = allNaN && isNaN(this.selectionData.hist[i])
      }
      return cnt > 0 && !allNaN
    },

    displayChart1a: function () {
      let cnt = this.selectionData.hist1a.length
      let allNaN = true
      for (let i = 0; i < cnt; i++) {
        allNaN = allNaN && isNaN(this.selectionData.hist1a[i])
      }
      return cnt > 0 && !allNaN
    },

    displayChart1b: function () {
      let cnt = this.selectionData.hist1a.length
      let allNaN = true
      for (let i = 0; i < cnt; i++) {
        allNaN = allNaN && isNaN(this.selectionData.hist1b[i])
      }
      return cnt > 0 && !allNaN
    },

    displayPanel: function () {
      return this.selectedHospital || this.selectedRegion || this.selectedTxCenter
    },

    region: function () {
      if (!this.selectedRegion) {
        return null
      }
      if (this.queryRegionMode === 'opo') {
        let cnt = this.opoShapes.features.length
        for (let i = 0; i < cnt; i++) {
          if (this.opoShapes.features[i].properties.OPO_ID === this.selectedRegion.opo_id) {
            return this.opoShapes.features[i].properties
          }
        }
      } else if (this.queryRegionMode === 'unos') {
        let cnt = this.unosShapes.features.length
        for (let i = 0; i < cnt; i++) {
          if (this.unosShapes.features[i].properties.UNOS_ID === this.selectedRegion.unos_id) {
            return this.unosShapes.features[i].properties
          }
        }
      } else if (this.queryRegionMode === 'state') {
        let cnt = this.stateShapes.features.length
        for (let i = 0; i < cnt; i++) {
          if (this.stateShapes.features[i].properties.STATE_ID === this.selectedRegion.state_id) {
            return this.stateShapes.features[i].properties
          }
        }
      }
      return null
    },

    hospital: function () {
      if (!this.selectedHospital) {
        return null
      }
    },

    txCenter: function () {
      if (!this.selectedTxCenter) {
        return null
      }
      let cnt = this.txCenters.length
      for (let i = 0; i < cnt; i++) {
        if (this.txCenters[i].tx_ctr_id === this.selectedTxCenter) {
          return this.txCenters[i]
        }
      }
      return null
    },

    title: function () {
      if (this.selectedTxCenter) {
        return this.txCenter.name
      }
      if (this.selectedHospital) {
        return this.selectedHospital
      }
      if (this.queryRegionMode === 'opo') {
        return 'OPO: ' + this.region.OPO_NAME
      } else if (this.queryRegionMode === 'unos') {
        return 'UNOS ' + this.region.UNOS_ID
      } else {
        return this.region.name + ' (' + this.region.STATE_ID + ')'
      }
    },

    subtitle: function () {
      return this.selectedStatistic.label
    },

    regionData: function () {
      let query = {
        organMode: this.queryOrganMode,
        regionMode: this.queryRegionMode,
        year: this.queryYear
      }
      switch (this.queryRegionMode) {
        case 'opo':
        case 'unos':
          let regionId = this.selectedRegion.opo_id || this.selectedRegion.unos_id
          return regionsStatFun['r_stat_' + this.selectedStatisticId](this.txRecords, query, regionId)

        case 'state':
          regionId = this.selectedRegion.state_id
          return regionsStatFun['r_stat_' + this.selectedStatisticId](this.hospitalRecords, query, this.selectedRegion.state_id)
      }
    },

    txCenterData: function () {
      let query = {
        organMode: this.queryOrganMode,
        regionMode: this.queryRegionMode,
        year: this.queryYear
      }
      return txCentersStatFun['tx_stat_' + this.selectedStatisticId](this.txRecords, query, this.txCenter.tx_ctr_id)
    },

    hospitalData: function () {
      let query = {
        organMode: this.queryOrganMode,
        regionMode: this.queryRegionMode,
        year: this.queryYear
      }
      return hospitalsStatFun['h_stat_' + this.selectedStatisticId](this.hospitalRecords, query, this.selectedHospital)
    },

    selectionData: function () {
      if (!this.queryStatisticId) {
        return []
      }

      if (this.selectedRegion) {
        return this.regionData
      } else if (this.selectedTxCenter) {
        return this.txCenterData
      } else if (this.selectedHospital) {
        return this.hospitalData
      }
    },

    chartData: function () {
      return {
        labels: this.selectionData.years,
        datasets: [
          {
            backgroundColor: '#25826a',
            data: this.selectionData.hist
          }
        ]
      }
    },

    chartData1a: function () {
      return {
        labels: this.selectionData.years,
        datasets: [
          {
            backgroundColor: '#67a84a',
            data: this.selectionData.hist1a
          }
        ]
      }
    },

    chartData1b: function () {
      return {
        labels: this.selectionData.years,
        datasets: [
          {
            backgroundColor: '#a8cd2a',
            data: this.selectionData.hist1b
          }
        ]
      }
    },

    chartData2: function () {
      return {
        labels: this.selectionData.years,
        datasets: [
          {
            backgroundColor: '#dfec10',
            data: this.selectionData.hist2
          }
        ]
      }
    },

    chartDataYmax: function () {
      let max = 0
      if (this.displayChart) {
        max = Math.max(max, ...this.selectionData.hist)
      }
      if (this.displayChart1a) {
        max = Math.max(max, ...this.selectionData.hist1a)
      }
      if (this.displayChart1b) {
        max = Math.max(max, ...this.selectionData.hist1b)
      }

      if (max < 10) {
        return Math.ceil(max)
      } else if (max < 100) {
        return Math.ceil(Math.round(max / 10) * 10)
      } else if (max < 1000) {
        return Math.ceil(Math.round(max / 100) * 100)
      }
      return max
    }
  },

  methods: {
    hidePanel: function () {
      this.$store.commit('selectRegion', null)
      this.$store.commit('selectHospital', null)
      this.$store.commit('selectTxCenter', null)
    }
  }
}
</script>

<style scoped>
    .graph-panel {
        box-sizing: border-box;
        position: absolute;
        bottom: 0;
        width: 100%;
        left: 0;
        padding: 0 25px 25px 25px;
        z-index: 9999;
    }

    .top-bar {
        display: flex;
        justify-content: space-between;
    }

    .title {
        padding: 12px 0 0 0;
        text-shadow: 1px 1px #25826a;
    }

    .subtitle {
        font-size: 95%;
        padding: 0 0 12px 0;
        text-shadow: 1px 1px #25826a;
    }

    .chart-title {
        color: #000;
        font-size: 90%;
        text-align: center;
        padding: 4px 0;
    }

    .chart-empty {
        display: flex;
        color: #999;
        height: 250px;
        justify-content: center;
        align-items: center;
    }

    .row {
        overflow: auto;
    }

    .col {
        width: 250px;
        margin-right: 12px;
        float: left;
        background: #fff;
    }
</style>



// WEBPACK FOOTER //
// src/components/Dialogs/GraphPanel.vue